import { Options, AuthProviderCallback, AuthenticationHandler, RetryHandler, HTTPMessageHandler, TelemetryHandler, RetryHandlerOptions, AuthProvider, Middleware, Context } from '@microsoft/microsoft-graph-client';
import { CustomAuthenticationProvider } from '@microsoft/microsoft-graph-client/lib/src/CustomAuthenticationProvider';
import { IAuthenticationProvider, AuthEnvironment, GetAccessTokenOptions } from '../interfaces/IAuthenticationProvider';
import { LogEventNames } from "../interfaces/ITelemetryLogger";
import { LogServiceEvent } from "../TelemetryLogger/LogServiceEvent";
import { RetryServiceCaller, RetryServiceCallUntilSuccessOrTimeout } from '../../backend/utils/Utils';
import { SpoUploadHandler } from './SpoUploadHandler';
import { enableUploadSessionNoTempAuth } from '../../utils/ChangeGate';

export function GraphBaseUrlFromAuthEnvironment(environment: AuthEnvironment): string {
    switch (environment) {
        case AuthEnvironment.PPE:
            return "https://graph.microsoft-ppe.com";
        case AuthEnvironment.MSIT:
            return "https://graph.microsoft.com";
        case AuthEnvironment.Prod:
            return "https://graph.microsoft.com";
        case AuthEnvironment.GCC:
            return "https://graph.microsoft.com";
        case AuthEnvironment.GCCHigh:
            return "https://graph.microsoft.us";
        case AuthEnvironment.DOD:
            return "https://dod-graph.microsoft.us";
        case AuthEnvironment.USNAT:
            return "https://graph.eaglex.ic.gov";
        case AuthEnvironment.USSEC:
            return "https://graph.microsoft.scloud";
        default:
            throw Error("Graph endpoint undefined for auth environment: " + environment);
    }
}

function CreateGraphAuthProvider(authProvider: IAuthenticationProvider): AuthProvider {
    const graphBaseUrl: string = GraphBaseUrlFromAuthEnvironment(authProvider.GetAuthEnvironment());
    return async (callback: AuthProviderCallback) => {
        try {
            const options: GetAccessTokenOptions = { resource: graphBaseUrl };
            const token: string = await authProvider.GetAccessToken(options);
            callback(null, token);
        }
        catch (e) {
            callback(e, null);
        }
    };
}

export function GetCustomHosts(provider: IAuthenticationProvider): Set<string> | undefined {
    const environment = provider.GetAuthEnvironment();

    if (environment === AuthEnvironment.USSEC) {
        return new Set(["graph.microsoft.scloud"]);
    } else if (environment === AuthEnvironment.USNAT) {
        return new Set(["graph.eaglex.ic.gov"]);
    } else {
        return undefined;
    }
}

export function GetAuthOptions(provider: IAuthenticationProvider): Options {
    const environment = provider.GetAuthEnvironment();

    return {
        authProvider: CreateGraphAuthProvider(provider),
        baseUrl: GraphBaseUrlFromAuthEnvironment(environment),
        customHosts: GetCustomHosts(provider)
    };
}

// A retry handler that will retry on exceptions
class ExceptionRetryHandler implements Middleware {
    private nextMiddleware: Middleware | null = null;
    private shouldRetry: (e: any) => boolean;
    private logEventName: LogEventNames;

    constructor(shouldRetry: (e: any) => boolean, logEventName: LogEventNames) {
        this.shouldRetry = shouldRetry;
        this.logEventName = logEventName;
    }

    public async execute (context: Context): Promise<void> {
        const logServiceEvent = new LogServiceEvent(this.logEventName);
        logServiceEvent.Start();
        await RetryServiceCallUntilSuccessOrTimeout(logServiceEvent, () => this.nextMiddleware!.execute(context), false /*shouldRetryOnNull*/, RetryServiceCaller.ExceptionRetryHandlerExecute, this.shouldRetry, () => true /*shouldIgnoreTimeout*/).promise;
    }

    public setNext(middleware: Middleware) {
        this.nextMiddleware = middleware;
    }
}

// Microsoft Graph JavaScript SDK default middleware chain is as follows:
// AuthenticationHandler -> SpoUploadHandler -> RetryHandler -> TelemetryHandler -> HTTPMessageHandler.
export function GetDefaultMiddlewareChain(authProvider: IAuthenticationProvider, enableTelemetryHandler: boolean, logEventName: LogEventNames) {
    const authenticationHandler = new AuthenticationHandler(new CustomAuthenticationProvider(CreateGraphAuthProvider(authProvider)));
    const exceptionRetryHandler = new ExceptionRetryHandler((e) => {
        return e instanceof TypeError && e.message == "Failed to fetch" // Retry on fetch errors, which are network errors
    }/*shouldRetry*/, logEventName);
    const retryHandler = new RetryHandler(new RetryHandlerOptions());
    const telemetryHandler = enableTelemetryHandler ? new TelemetryHandler() : null;
    const httpMessageHandler = new HTTPMessageHandler();

    // Chain: AuthenticationHandler -> SpoUploadHandler -> ExceptionRetryHandler -> RetryHandler -> [TelemetryHandler] -> HTTPMessageHandler
    if (enableUploadSessionNoTempAuth) {
        const spoUploadHandler = new SpoUploadHandler(authProvider);
        authenticationHandler.setNext(spoUploadHandler);
        spoUploadHandler.setNext(exceptionRetryHandler);
    } else {
        authenticationHandler.setNext(exceptionRetryHandler);
    }
    exceptionRetryHandler.setNext(retryHandler);
    retryHandler.setNext(telemetryHandler ?? httpMessageHandler);

    if (telemetryHandler)
        telemetryHandler.setNext(httpMessageHandler);

    return authenticationHandler;
}